import { ValidationErrorMessages } from '../constants';

/**
 * Validates a URL and optionally checks for required query parameters
 * @param url - The URL to validate
 * @param requiredParams - Optional array of required query parameter names
 * @returns An object containing validation result and error message
 */
export interface UrlValidationResult {
  isValid: boolean;
  message: string | null;
  params: { [key: string]: string };
}

export const isValidUrl = (
  url: string,
  fieldName: string,
  requiredParams?: string[]
): UrlValidationResult => {
  const result: UrlValidationResult = {
    fieldName,
    isValid: false,
    message: null,
    params: {},
  };

  if (!url || url.trim() === '') {
    result.message = ValidationErrorMessages.URL_REQUIRED.replace(
      '${field}',
      fieldName
    );
    return result;
  }

  const trimmedUrl = url.trim();

  // Check if URL starts with http:// or https://
  if (!trimmedUrl.match(/^https?:\/\//i)) {
    result.message = ValidationErrorMessages.URL_PROTOCOL_INVALID.replace(
      '${field}',
      fieldName
    );
    return result;
  }

  try {
    const urlObj = new URL(trimmedUrl);

    // Basic URL validation passed
    result.isValid = true;

    // If required params are specified, check for them
    if (requiredParams && requiredParams.length > 0) {
      const searchParams = urlObj.searchParams;

      // Get all params
      searchParams.forEach((value, key) => {
        result.params[key] = value;
      });

      // Check for required params and their values
      const missingOrEmptyParams = requiredParams.filter(
        (param) => !searchParams.has(param) || !searchParams.get(param)?.trim()
      );

      if (missingOrEmptyParams.length > 0) {
        result.isValid = false;
        result.message = ValidationErrorMessages.URL_MISSING_PARAMS.replace(
          '${field}',
          fieldName
        ).replace('${params}', missingOrEmptyParams.join(', '));
      }
    }
  } catch (error) {
    result.isValid = false;
    result.message = ValidationErrorMessages.URL_INVALID.replace(
      '${field}',
      fieldName
    );
  }

  return result;
};
